<template>
  <CreateUpdateTemplate
    :routePreventDialog="routePreventDialog"
    :customClass="'opportunity-create create-form'"
    v-if="
      getPermission('opportunities:create') ||
      getPermission('opportunities:update')
    "
  >
    <template v-slot:header-title>
      <div class="d-flex justify-space-between align-center">
        <h1 class="form-title text-truncate d-flex align-center">
          <span>Opportunity for</span>

          <div
            class="ml-3 form-title-link text-h5"
            :class="{
              'cursor-pointer': !isEditMode,
              'disabled-link': isEditMode,
            }"
            @click="!isEditMode && openDialog(opportunityType)"
          >
            <template v-if="selectedEntityName">
              {{ selectedEntityName }}
            </template>

            <template v-else>
              Select a {{ opportunityTypeLabel }}

              <v-icon large color="cyan" class="ml-1">
                mdi-account-check-outline
              </v-icon>
            </template>
          </div>
        </h1>

        <v-chip
          label
          color="chip-custom-blue"
          outlined
          class="text-white p-3 mr-4"
          style="font-size: 21px !important; font-weight: 700"
        >
          {{ opportunityData.barcode }}
        </v-chip>
      </div>
    </template>

    <template v-slot:header-action>
      <v-btn
        v-on:click="goBack"
        :disabled="formLoading"
        outlined
        color="grey darken-2"
        class="mx-2 custom-grey-border custom-bold-button rounded-lg px-4 text-capitalize"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        Cancel
      </v-btn>

      <v-btn
        :disabled="!formValid || formLoading || editDataLoading"
        :loading="formLoading || editDataLoading"
        depressed
        color="cyan darken-1"
        class="mx-2 custom-bold-button white--text rounded-lg px-6 text-capitalize"
        v-on:click="onSubmit"
      >
        <v-icon left small>mdi-content-save-outline</v-icon>
        {{ isEditMode ? "Update Draft" : "Save Draft" }}
      </v-btn>
    </template>

    <template v-slot:body>
      <perfect-scrollbar
        :options="{ suppressScrollX: true }"
        class="scroll custom-box-top-inner-shado"
        style="height: calc(100vh - 150px); position: relative"
      >
        <v-form
          ref="opportunityForm"
          v-model.trim="formValid"
          lazy-validation
          v-on:submit.stop.prevent="onSubmit"
        >
          <!-- Premium Segmented Type Selector Cards (Custom Styles) -->
          <v-container fluid class="py-0">
            <v-row dense>
              <!-- LEAD OPTION CARD -->
              <v-col cols="12" md="6" class="pt-1">
                <div
                  class="segmented-type-card"
                  :class="{
                    'is-lead-active': opportunityType === 'lead',
                    'is-disabled': isEditMode,
                  }"
                  @click="!isEditMode && switchOpportunityType('lead')"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <div class="type-icon-box lead-icon mr-3">
                        <v-icon
                          :color="
                            opportunityType === 'lead' ? 'white' : '#0284c7'
                          "
                          medium
                        >
                          mdi-account-search-outline
                        </v-icon>
                      </div>

                      <div>
                        <div class="d-flex align-center">
                          <span class="type-title-text mr-2">
                            Lead / Prospect
                          </span>
                          <v-chip
                            x-small
                            :color="
                              opportunityType === 'lead'
                                ? 'blue darken-2'
                                : 'blue lighten-4'
                            "
                            :text-color="
                              opportunityType === 'lead'
                                ? 'white'
                                : 'blue darken-3'
                            "
                            class="font-weight-bold px-2"
                          >
                            PRE-SALES
                          </v-chip>
                        </div>
                        <div class="type-subtitle-text">
                          Create opportunity for prospect or new lead
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-center">
                      <v-chip
                        v-if="opportunityType === 'lead'"
                        small
                        color="blue darken-2"
                        text-color="white"
                        class="font-weight-bold px-2"
                      >
                        <v-icon left x-small color="white"
                          >mdi-check-circle</v-icon
                        >
                        Selected
                      </v-chip>
                      <v-icon v-else color="#cbd5e1" medium>
                        mdi-circle-outline
                      </v-icon>
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- CUSTOMER OPTION CARD -->
              <v-col cols="12" md="6" class="pt-1">
                <div
                  class="segmented-type-card"
                  :class="{
                    'is-customer-active': opportunityType === 'customer',
                    'is-disabled': isEditMode,
                  }"
                  @click="!isEditMode && switchOpportunityType('customer')"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <div class="type-icon-box customer-icon mr-3">
                        <v-icon
                          :color="
                            opportunityType === 'customer' ? 'white' : '#10b981'
                          "
                          medium
                        >
                          mdi-domain
                        </v-icon>
                      </div>

                      <div>
                        <div class="d-flex align-center">
                          <span class="type-title-text mr-2">
                            Customer Account
                          </span>
                          <v-chip
                            x-small
                            :color="
                              opportunityType === 'customer'
                                ? 'green darken-2'
                                : 'green lighten-4'
                            "
                            :text-color="
                              opportunityType === 'customer'
                                ? 'white'
                                : 'green darken-3'
                            "
                            class="font-weight-bold px-2"
                          >
                            EXISTING CLIENT
                          </v-chip>
                        </div>
                        <div class="type-subtitle-text">
                          Create opportunity for active customer account
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-center">
                      <v-chip
                        v-if="opportunityType === 'customer'"
                        small
                        color="green darken-2"
                        text-color="white"
                        class="font-weight-bold px-2"
                      >
                        <v-icon left x-small color="white"
                          >mdi-check-circle</v-icon
                        >
                        Selected
                      </v-chip>
                      <v-icon v-else color="#cbd5e1" medium>
                        mdi-circle-outline
                      </v-icon>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <v-row>
            <!-- LEFT COLUMN: Basic Details & Contact Details -->
            <v-col md="7">
              <v-container fluid>
                <v-card
                  flat
                  class="custom-grey-border remove-border-radius mb-4"
                >
                  <v-card-title class="headline grey lighten-4 py-2">
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue mb-0"
                    >
                      Basic Details
                    </h3>
                  </v-card-title>
                  <v-card-text class="p-6">
                    <div class="pb-2">
                      <label
                        for="title"
                        class="required font-size-16 font-weight-500"
                        >Title</label
                      >
                      <TextField
                        id="title"
                        dense
                        filled
                        placeholder="Title"
                        solo
                        flat
                        counter="100"
                        v-model="opportunityData.title"
                        color="cyan"
                        :maxlength="100"
                        :rules="[
                          validateRules.required(
                            opportunityData.title,
                            'Title'
                          ),
                        ]"
                      />
                    </div>

                    <label for="remark" class="font-size-16 font-weight-500"
                      >Remark</label
                    >
                    <TextAreaField
                      auto-grow
                      dense
                      filled
                      color="cyan"
                      v-model="opportunityData.remark"
                      placeholder="Remark"
                      solo
                      flat
                      row-height="25"
                      counter="250"
                    />
                  </v-card-text>
                </v-card>
              </v-container>

              <v-container fluid class="pt-0">
                <v-card
                  flat
                  outlined
                  class="rounded-lg border-slate bg-slate-50 pa-3 h-100 shadow-0"
                >
                  <div
                    class="d-flex align-center justify-space-between pb-2 mb-2 border-bottom border-slate-100"
                  >
                    <div class="d-flex align-center">
                      <v-icon small color="cyan darken-2" class="mr-2"
                        >mdi-account-outline</v-icon
                      >
                      <span
                        class="font-weight-700 color-custom-blue font-size-14 text-uppercase"
                      >
                        Contact Details
                      </span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      color="cyan"
                      class="bg-white custom-grey-border"
                      v-on:click="openDialog('contact')"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                  <template v-if="contactPersonId">
                    <div
                      class="d-flex align-center mb-1 font-size-14 font-weight-600 color-custom-blue"
                    >
                      <v-icon small color="slate-600" class="mr-2"
                        >mdi-account-circle-outline</v-icon
                      >
                      {{ vContactPerson.display_name }}
                    </div>
                    <div
                      v-if="vContactPerson.primary_phone"
                      class="d-flex align-center mb-1 font-size-13 text-slate-700"
                    >
                      <v-icon small color="slate-500" class="mr-2"
                        >mdi-phone-outline</v-icon
                      >
                      {{ vContactPerson.primary_phone }}
                    </div>
                    <div
                      v-if="vContactPerson.primary_email"
                      class="d-flex align-center mb-0 font-size-13 text-slate-700"
                    >
                      <v-icon small color="slate-500" class="mr-2"
                        >mdi-email-outline</v-icon
                      >
                      {{ vContactPerson.primary_email }}
                    </div>
                  </template>
                  <template v-else>
                    <div
                      class="d-flex align-center text-slate-500 font-size-13 py-2"
                    >
                      <v-icon small color="slate-400" class="mr-2"
                        >mdi-account-circle-outline</v-icon
                      >
                      <span>Please select contact person</span>
                    </div>
                  </template>
                </v-card>
              </v-container>
            </v-col>

            <!-- RIGHT COLUMN: Opportunity Settings -->
            <v-col md="5">
              <v-container fluid>
                <v-card flat class="custom-grey-border remove-border-radius">
                  <v-card-title class="headline grey lighten-4 py-2">
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue mb-0"
                    >
                      Opportunity Settings
                    </h3>
                  </v-card-title>
                  <v-card-text class="p-6 font-size-16">
                    <table width="100%" style="table-layout: fixed">
                      <tr>
                        <td width="150" class="font-size-16 font-weight-500">
                          <label for="source" class="required">Source</label>
                        </td>
                        <td>
                          <v-autocomplete
                            id="source"
                            hide-details
                            v-model.trim="opportunityData.source"
                            :items="sourceList"
                            :rules="[
                              validateRules.required(
                                opportunityData.source,
                                'source'
                              ),
                            ]"
                            dense
                            flat
                            filled
                            placeholder="Source"
                            item-color="cyan"
                            color="cyan"
                            solo
                            item-value="value"
                            item-text="text"
                          >
                            <template v-slot:item="{ item }">
                              <div class="d-flex align-center">
                                <v-icon small color="cyan" class="mr-2">
                                  {{ item.icon }}
                                </v-icon>
                                <span>{{ item.text }}</span>
                              </div>
                            </template>

                            <template v-slot:no-data>
                              <v-list-item>
                                <v-list-item-title>
                                  No Source(s) Found.
                                </v-list-item-title>
                              </v-list-item>
                            </template>
                          </v-autocomplete>
                        </td>
                      </tr>
                      <tr>
                        <td width="150" class="font-size-16 font-weight-500">
                          <label for="opportunity">Opportunity</label>
                        </td>
                        <td>
                          <v-radio-group
                            v-model="opportunityData.opportunity"
                            row
                            hide-details
                          >
                            <v-radio
                              label="High Chance"
                              value="High Chance"
                              color="cyan"
                            ></v-radio>
                            <v-radio
                              label="50 50"
                              value="50 50"
                              color="cyan"
                            ></v-radio>
                            <v-radio
                              label="Low Chance"
                              value="Low Chance"
                              color="cyan"
                            ></v-radio>
                            <v-radio
                              label="None"
                              value="None"
                              color="cyan"
                            ></v-radio>
                          </v-radio-group>
                        </td>
                      </tr>
                      <tr>
                        <td width="150" class="font-size-16 font-weight-500">
                          <label for="opportunity_date">Opportunity Date</label>
                        </td>
                        <td>
                          <v-menu
                            v-model="datePicker"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                id="opportunity_date"
                                v-model.trim="opportunityData.opportunity_date"
                                dense
                                filled
                                placeholder="Opportunity Date"
                                solo
                                flat
                                color="cyan"
                                prepend-inner-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="opportunityData.opportunity_date"
                              color="cyan"
                              @input="datePicker = false"
                            ></v-date-picker>
                          </v-menu>
                        </td>
                      </tr>
                      <tr>
                        <td width="150" class="font-size-16 font-weight-500">
                          <label for="rating">Rate Opportunity</label>
                        </td>
                        <td>
                          <v-rating
                            id="rating"
                            v-model="opportunityData.rating"
                            dense
                            color="orange"
                            background-color="orange lighten-3"
                          ></v-rating>
                        </td>
                      </tr>
                    </table>
                  </v-card-text>
                </v-card>
              </v-container>
            </v-col>
          </v-row>

          <!-- Line Items Section -->
          <v-col cols="12" ref="lineItem">
            <v-container fluid class="pa-0">
              <v-card flat class="custom-grey-border remove-border-radius">
                <v-card-title
                  class="headline grey lighten-4 cursor-pointer"
                  @click="show_line_items = !show_line_items"
                >
                  <h3
                    class="font-weight-700 custom-headline color-custom-blue mb-0"
                  >
                    <v-icon>{{
                      !show_line_items ? "mdi-chevron-down" : "mdi-chevron-up"
                    }}</v-icon>
                    Line Items
                  </h3>
                </v-card-title>
                <v-expand-transition>
                  <v-card-text
                    v-show="show_line_items"
                    ref="lineItem"
                    class="font-size-16 py-0"
                  >
                    <LineItem
                      v-if="related_type > 0"
                      :related-type="related_type"
                      is-opportunity
                      :unit="unit"
                      :related-detail="updatedOpportunityData"
                      :db-line-items="dbLineItems"
                      :db-equipments="dbEquipments"
                      :is-customer-required="true"
                      is-quotation
                      v-model="opportunityData.line_items"
                    />
                  </v-card-text>
                </v-expand-transition>
              </v-card>
            </v-container>
          </v-col>

          <!-- Notes Section -->
          <v-col cols="12" ref="qtNote">
            <v-container fluid class="pa-0">
              <v-card flat class="custom-grey-border remove-border-radius">
                <v-card-title
                  class="headline grey lighten-4 cursor-pointer"
                  @click="show_notes = !show_notes"
                >
                  <h3
                    class="font-weight-700 custom-headline color-custom-blue mb-0"
                  >
                    <v-icon>{{
                      !show_notes ? "mdi-chevron-down" : "mdi-chevron-up"
                    }}</v-icon>
                    Notes
                  </h3>
                </v-card-title>
                <v-expand-transition>
                  <v-card-text class="p-6 font-size-16" v-show="show_notes">
                    <v-layout wrap>
                      <v-flex md12>
                        <label class="font-size-16 font-weight-600">Note</label>
                        <tiny-mce
                          :disabled="formLoading"
                          v-model="opportunityData.admin_note"
                        />
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-expand-transition>
              </v-card>
            </v-container>
          </v-col>

          <!-- Attachments Section -->
          <v-col cols="12" ref="qtAttachment">
            <v-container fluid class="pa-0">
              <CollapsibleDocumnetList
                :extensive="false"
                v-model="opportunityData.attachments"
                :document-type="docType"
              >
              </CollapsibleDocumnetList>
            </v-container>
          </v-col>
        </v-form>
      </perfect-scrollbar>

      <PersonSelectOpportunity
        :dialog="contactDialog"
        v-on:close="contactDialog = false"
        :parentId="opportunityType === 'customer' ? customerId : leadId"
        :type="opportunityType"
        @select="selectContactPerson"
      />
      <EntitySelectDialog
        :dialog="customerDialog"
        type="customer"
        @close="customerDialog = false"
        @select="selectCustomer"
      />
      <EntitySelectDialog
        :dialog="leadDialog"
        type="lead"
        @close="leadDialog = false"
        @select="selectLead"
      />
    </template>
  </CreateUpdateTemplate>
</template>
<script>
import CommonMixin from "@/core/plugins/common-mixin";
import ValidationMixin from "@/core/plugins/validation-mixin";
import CreateUpdateTemplate from "@/view/pages/partials/Create-Update-Template.vue";
import LineItem from "@/view/pages/partials/Line-Item-V2.vue";
// import SystemGenerateId from "@/view/pages/partials/SystemGenerateId.vue";
import EntitySelectDialog from "@/view/pages/partials/EntitySelectDialog.vue";
import CollapsibleDocumnetList from "@/view/pages/CollapsibleDocumnetList";
import TinyMCE from "@/view/pages/partials/TinyMCE.vue";
// import DocumnetList from "@/view/pages/DocumnetList";
import TextAreaField from "@/view/components/app-component/TextAreaField.vue";
import PersonSelectOpportunity from "@/view/components/PersonSelectOpportunity.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  POST,
  CLEAR_ERROR,
  QUERY,
  GET,
  PUT,
} from "@/core/services/store/request.module";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import { PreventDialogEventBus } from "@/core/lib/prevent.reload.lib";
import { mapGetters } from "vuex";
import { SET_CUSTOMER } from "@/core/services/store/visit.module";

export default {
  mixins: [CommonMixin, ValidationMixin],
  name: "opportunity-create",
  title: "Create Opportunity",
  data() {
    return {
      isEditMode: false,
      editId: null,
      opportunityId: 0,
      opportunity: {},
      barcode: null,
      formValid: false,
      formLoading: false,
      editDataLoading: false,
      routePreventDialog: false,
      show_line_items: false,
      show_notes: false,
      show_attachments: false,
      datePicker: false,
      contactDialog: false,
      customerDialog: false,
      leadDialog: false,
      entityDialog: false,
      selectedEntityName: "",
      related_type: 9,
      unit: [],
      updatedOpportunityData: {},
      dbLineItems: [],
      dbEquipments: [],
      vContactPerson: null,
      contactPersonId: null,
      leadId: 0,
      customerId: 0,
      opportunityType: "lead",
      docType: 21,
      opportunityData: {
        id: null,
        title: null,
        barcode: null,
        remark: null,
        source: null,
        line_items: [],
        opportunity: null,
        opportunity_date: null,
        rating: 0,
        admin_note: null,
        client_note: null,
        attachments: [],
      },
      sourceList: [
        {
          text: "Web Enquiry",
          value: "Web Enquiry",
          icon: "mdi-web",
        },
        {
          text: "Web Chat",
          value: "Web Chat",
          icon: "mdi-chat",
        },
        {
          text: "Email Marketer",
          value: "Email Marketer",
          icon: "mdi-email",
        },
        {
          text: "Tele Call",
          value: "Tele Call",
          icon: "mdi-phone",
        },
        {
          text: "Google Adwords",
          value: "Google Adwords",
          icon: "mdi-google",
        },
        {
          text: "Fsm/Ams",
          value: "Fsm/Ams",
          icon: "mdi-cog",
        },
        {
          text: "Facebook",
          value: "Facebook",
          icon: "mdi-facebook",
        },
        {
          text: "Referral",
          value: "Referral",
          icon: "mdi-account-group",
        },
        {
          text: "Intern",
          value: "Intern",
          icon: "mdi-school",
        },
        {
          text: "Canvassing Team",
          value: "Canvassing Team",
          icon: "mdi-account-multiple",
        },
        {
          text: "None",
          value: "None",
          icon: "mdi-minus-circle-outline",
        },
      ],
    };
  },
  watch: {
    formValid(param) {
      if (param) {
        this.$store.dispatch(CLEAR_ERROR, {});
      }
    },
  },
  methods: {
    selectCustomer(payload) {
      this.customerDialog = false;
      const customer = payload.customer;
      this.customerId = customer.id;
      this.selectedEntityName = customer.display_name || customer.name || "";
      this.opportunityType = "customer";
      this.$store.dispatch(SET_CUSTOMER, { id: customer.id }).catch((error) => {
        console.error("Unable to set selected customer in store:", error);
      });

      // Auto-select default contact person
      if (payload.contact_person) {
        this.selectContactPerson({
          ...payload.contact_person,
          primary_phone:
            payload.contact_person.primary_phone ||
            payload.contact_person.phone,
          primary_email:
            payload.contact_person.primary_email ||
            payload.contact_person.email,
        });
      } else {
        this.autoSelectDefaultContactPerson(customer.id);
      }
    },
    selectLead(payload) {
      this.leadDialog = false;
      const lead = payload.customer;
      this.leadId = lead.id;
      this.selectedEntityName = lead.display_name || lead.name || "";
      this.opportunityType = "lead";
      this.customerId = 0;
      this.$store.commit(SET_CUSTOMER, {});

      // Auto-select default contact person
      if (payload.contact_person) {
        this.selectContactPerson({
          ...payload.contact_person,
          primary_phone:
            payload.contact_person.primary_phone ||
            payload.contact_person.phone,
          primary_email:
            payload.contact_person.primary_email ||
            payload.contact_person.email,
        });
      } else {
        this.autoSelectDefaultContactPerson(lead.id);
      }
    },
    async autoSelectDefaultContactPerson(parentId) {
      if (!parentId) return;
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: `contact-person-list/${parentId}?type=${this.opportunityType}`,
        });
        const persons = data || [];
        if (persons.length) {
          const first = persons[0];
          this.selectContactPerson({
            ...first,
            primary_phone: first.primary_phone || first.phone,
            primary_email: first.primary_email || first.email,
          });
        } else {
          this.vContactPerson = null;
          this.contactPersonId = null;
        }
      } catch (error) {
        console.error("Unable to auto-select contact person:", error);
      }
    },
    getWarranty(type) {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, {
          url: "warranty/" + _this.warranty,
        })
        .then(({ data }) => {
          _this.warrantyArr = data;
          if (type && type == "extend") {
            this.extentWarrantyDialog = true;
          }
          if (type && type == "renew") {
            this.renewWarrantyDialog = true;
          }
          _this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Warranty", route: "warranty" },
            { title: "Detail" },
            { title: data.barcode },
          ]);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    async initEditMode() {
      const editId = this.$route.query.edit;
      if (editId) {
        this.isEditMode = true;
        this.editId = editId;
        await this.fetchOpportunityData(editId);
      }
    },
    async fetchOpportunityData(id) {
      this.editDataLoading = true;
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: `opportunity/${id}`,
        });
        //
        // Prefill all fields
        this.opportunityData = {
          id: data.id,
          barcode: data.barcode,
          title: data.title,
          remark: data.remark,
          source: data.source,
          opportunity: data.opportunity_text,
          opportunity_date: data.opportunity_date,
          rating: Number(data.rating) || 0,
          admin_note: data.admin_note,
          client_note: data.client_note,
          line_items: data.line_items ?? [],
          attachments: data.attachments ?? [],
        };

        this.dbLineItems = data.line_items ?? [];
        this.dbEquipments = data.equipments ?? [];
        this.updatedOpportunityData = data;
        this.updatedOpportunityData.line_item = data.line_items ?? [];

        if (data.contactPerson) {
          this.selectContactPerson(data.contactPerson);
        }
        this.isEditMode = true;
      } catch (e) {
        console.error("Failed to fetch opportunity:", e);
      } finally {
        this.editDataLoading = false;
      }
    },

    async get_attributes() {
      try {
        const response = await this.$store.dispatch(QUERY, {
          url: "opportunity/options",
          data: {
            lead_id: this.leadId,
          },
        });

        // Response print for debug
        console.log("get_attributes response:", response);

        // Barcode set
        if (!this.isEditMode && response?.data?.barcode) {
          this.opportunityData.barcode = response.data.barcode;
        }
        // Other dropdown unit list etc.
        if (response?.data?.unit) {
          this.unit = response.data.unit;
        }
      } catch (error) {
        console.error("get_attributes() error:", error);
        ErrorEventBus.$emit(
          "update:error",
          "Unable to load opportunity options"
        );
      }
    },

    updateLineItems(lineItems) {
      console.log("updateLineItems called with:", lineItems);
      this.opportunityData.line_items = lineItems;
    },
    openDialog(type) {
      if (type == "contact") {
        this.contactDialog = true;
      }
      if (type === "customer") {
        this.customerDialog = true;
      }
      if (type === "lead") {
        this.leadDialog = true;
      }
    },
    switchOpportunityType(type) {
      if (this.isEditMode) {
        return;
      }
      if (this.opportunityType === type) return;
      this.opportunityType = type;
      this.selectedEntityName = "";
      this.customerId = 0;
      this.leadId = 0;
      this.vContactPerson = null;
      this.contactPersonId = null;

      const query = { ...this.$route.query, type };
      delete query.lead;
      delete query.customer;
      delete query.person;
      this.$router.replace({ query });
    },
    selectContactPerson(row) {
      this.$set(this, "vContactPerson", {
        ...row,
        primary_phone: row.primary_phone || row.phone,
        primary_email: row.primary_email || row.email,
      });
      this.contactPersonId = row.id;
    },
    async fetchSelectedEntity() {
      let url = "";

      if (this.opportunityType === "customer" && this.customerId) {
        url = `customer/${this.customerId}`;
      }

      if (this.opportunityType === "lead" && this.leadId) {
        url = `leads/${this.leadId}`;
      }

      if (!url) return;

      const { data } = await this.$store.dispatch(GET, { url });

      this.selectedEntityName = data?.display_name || data?.lead_name || "";
    },
    getPerson(id) {
      this.$store
        .dispatch(QUERY, {
          url: `contact-person/${id}`,
          data: {
            type: this.opportunityType,
          },
        })
        .then((response) => {
          this.selectContactPerson(response.data);
        })
        .catch(() => {
          // handle error
        });
    },
    async onSubmit() {
      const allHeaders = this.vSelectedLineItem.every(
        (obj) => obj.type === "header"
      );
      if (
        !this.vSelectedLineItem ||
        !this.vSelectedLineItem.length ||
        allHeaders
      ) {
        ErrorEventBus.$emit("update:error", "Select Line Item");
        return false;
      }
      const AllItems = this.vSelectedLineItem;
      if (AllItems?.length > 0) {
        for (var k = 0; k < AllItems.length; k++) {
          if (!AllItems[k].product) {
            ErrorEventBus.$emit(
              "update:error",
              AllItems[k].type + " title is required."
            );
            return false;
          }
        }
      }

      const _this = this;

      try {
        if (!_this.validateEntitySelection()) {
          return false;
        }

        const formErrors = _this.validateForm(_this.$refs.opportunityForm);
        if (formErrors && formErrors.length) {
          for (let i = 0; i < formErrors.length; i++) {
            ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
          }
          return false;
        }

        if (!_this.$refs.opportunityForm.validate()) {
          return false;
        }

        _this.$store.dispatch(CLEAR_ERROR, {});

        console.log("opportunityData:", _this.opportunityData);

        const calc = _this.vCalculations || {};

        const formData = {
          barcode: _this.opportunityData.barcode,
          title: _this.opportunityData.title,
          remark: _this.opportunityData.remark,
          source: _this.opportunityData.source,
          "line-item": _this.vSelectedLineItem,
          opportunity_text: _this.opportunityData.opportunity,
          opportunity_date: _this.opportunityData.opportunity_date,
          rating: Number(_this.opportunityData.rating) || 0,
          contact_person: _this.contactPersonId,
          lead_id: _this.opportunityType === "lead" ? _this.leadId : null,
          customer_id:
            _this.opportunityType === "customer" ? _this.customerId : null,
          type: _this.opportunityType,
          admin_note: _this.opportunityData.admin_note,
          client_note: _this.opportunityData.client_note,
          attachments: _this.opportunityData.attachments,

          calculation: calc,
        };

        _this.formLoading = true;
        let responseData = null;

        const apiLeadId =
          _this.opportunityType === "customer" ? 0 : _this.leadId;

        if (this.isEditMode) {
          const { data } = await _this.$store.dispatch(PUT, {
            url: `opportunities/${apiLeadId}/${this.editId}`,
            data: formData,
          });
          responseData = data;
          _this.opportunityId = this.editId;
        } else {
          const { data } = await _this.$store.dispatch(POST, {
            url: `opportunities/${apiLeadId}`,
            data: formData,
          });
          responseData = data;
          _this.opportunityId = responseData?.id || 0;
        }

        // clear loading before route change
        _this.formLoading = false;

        // if (_this.opportunityType === "customer" && _this.customerId > 0) {
        //   _this.$router.push({
        //     name: "admin.customer.detail",
        //     params: { id: _this.customerId },
        //     query: { tab: "opportunities" },
        //   });
        // } else {
        //   _this.$router.push({
        //     name: "admin.lead.detail",
        //     params: { id: _this.leadId },
        //     query: { tab: "opportunities" },
        //   });
        // }
        if (_this.$route.query.return_to === "lead-list") {
          await _this.$router.push({ name: "admin.lead" });
        } else {
          _this.goBack();
        }
      } catch (error) {
        ErrorEventBus.$emit("update:error", InitializeError(error));
        _this.formLoading = false;
        return false;
      }
    },
    validateEntitySelection() {
      if (this.opportunityType === "customer" && !this.customerId) {
        ErrorEventBus.$emit("update:error", "Please select customer.");
        return false;
      }
      if (this.opportunityType === "lead" && !this.leadId) {
        ErrorEventBus.$emit("update:error", "Please select lead.");
        return false;
      }
      return true;
    },
  },
  components: {
    CreateUpdateTemplate,
    LineItem,
    // SystemGenerateId,
    CollapsibleDocumnetList,
    "tiny-mce": TinyMCE,
    // DocumnetList,
    TextAreaField,
    PersonSelectOpportunity,
    EntitySelectDialog,
  },
  created() {
    const routeType = (this.$route.query.type || "lead")
      .toString()
      .toLowerCase();
    this.opportunityType = routeType;

    if (routeType === "customer") {
      this.customerId = Number(this.$route.query.customer ?? 0);
      this.leadId = 0;
      if (this.customerId) {
        this.$store
          .dispatch(SET_CUSTOMER, { id: this.customerId })
          .catch((error) => {
            console.error(
              "Unable to set customer state from route query:",
              error
            );
          });
      }
    } else {
      this.leadId = Number(this.$route.query.lead ?? 0);
      this.customerId = 0;
      this.$store.commit(SET_CUSTOMER, {});
    }

    const personId = Number(this.$route.query.person ?? 0);
    if (personId) {
      this.getPerson(personId);
    }
    this.fetchSelectedEntity();
  },
  async mounted() {
    await this.initEditMode();
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Opportunity", route: "lead" },
      { title: this.isEditMode ? "Update" : "Create" },
    ]);
    this.get_attributes();
  },

  beforeRouteLeave(to, from, next) {
    const _this = this;
    if (_this.opportunityId > 0) {
      next();
    } else {
      _this.routePreventDialog = true;
      PreventDialogEventBus.$on("stay-on-page", function () {
        _this.routePreventDialog = false;
        next(false);
      });
      PreventDialogEventBus.$on("leave-page", function () {
        _this.routePreventDialog = false;
        next();
      });
    }
  },
  computed: {
    ...mapGetters(["vSelectedLineItem", "vCalculations"]),
    opportunityTypeLabel() {
      return this.opportunityType && this.opportunityType.toString().trim()
        ? this.opportunityType.charAt(0).toUpperCase() +
            this.opportunityType.slice(1)
        : "Lead";
    },
    isCustomerSelectionMissing() {
      return this.opportunityType === "customer" && !this.customerId;
    },
  },
};
</script>
<style lang="scss" scoped>
.disabled-card {
  pointer-events: none;
  opacity: 0.6;
}

.disabled-link {
  pointer-events: none;
  opacity: 0.7;
}

/* Custom Segmented Type Selector Cards Styling */
.segmented-type-card {
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  padding: 10px 14px;
  background: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover:not(.is-disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  }

  &.is-lead-active {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 1px solid #0284c7;
    box-shadow: 0 4px 14px rgba(2, 132, 199, 0.15);
  }

  &.is-customer-active {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    border: 1px solid #10b981;
    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.15);
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: #f8fafc;
  }
}

.type-icon-box {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &.lead-icon {
    background: #e0f2fe;
    color: #0284c7;

    .is-lead-active & {
      background: #0284c7;
      color: #ffffff;
    }
  }

  &.customer-icon {
    background: #d1fae5;
    color: #10b981;

    .is-customer-active & {
      background: #10b981;
      color: #ffffff;
    }
  }
}

.type-title-text {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.type-subtitle-text {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}
</style>
